import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import {
  garbageDisposalTypeTranslations,
  houseAccessTypeTranslations,
  houseTypeTranslations,
  housingSituationTranslations,
  landPossessionTypeTranslations,
  locationTypeTranslations,
  propertyTypeTranslations,
  sanitationTypeTranslations,
  wallMaterialTypeTranslations,
  waterSupplyTypeTranslations,
  waterTreatmentTypeTranslations,
} from '../mock/residence.data'
import residenceStatsAction from './residenceStats.action.json' with { type: 'json' }
import { z } from 'zod'
import { enumerated } from '~/prix/types/enumerated'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'

export const residenceExpressionForCharacteristics = z.object({
  originalId: z.string().describe('ID original'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  locationType: enumerated(locationTypeTranslations)
    .describe('Localização do domicílio')
    .optional(),
  hasElectricity: z.boolean().describe('Disponibilidade de energia').optional(),
  animalCount: z.number().describe('Quantidade de animais no domicílio').optional(),
  residentCount: z.number().describe('Quantidade de moradores').optional(),
  roomCount: z.number().describe('Número de cômodos').optional(),
  hasRegistrationRefusal: z.boolean().describe('Recusa de cadastro').optional(),
  propertyType: enumerated(propertyTypeTranslations).describe('Tipo de imóvel').optional(),
  housingSituation: enumerated(housingSituationTranslations)
    .describe('Situação da moradia')
    .optional(),
  houseType: enumerated(houseTypeTranslations).describe('Tipo de domicílio').optional(),
  landPossessionType: enumerated(landPossessionTypeTranslations)
    .describe('Posse da terra')
    .optional(),
  houseAccessType: enumerated(houseAccessTypeTranslations)
    .describe('Acesso ao domicílio')
    .optional(),
  wallMaterialType: enumerated(wallMaterialTypeTranslations)
    .describe('Material da parede')
    .optional(),
  waterSupplyType: enumerated(waterSupplyTypeTranslations)
    .describe('Abastecimento de água')
    .optional(),
  waterTreatmentType: enumerated(waterTreatmentTypeTranslations)
    .describe('Tratamento de água')
    .optional(),
  sanitationType: enumerated(sanitationTypeTranslations)
    .describe('Escoamento sanitário')
    .optional(),
  garbageDisposalType: enumerated(garbageDisposalTypeTranslations)
    .describe('Destino do lixo')
    .optional(),
  hasAnimal: z.boolean().describe('Presença de animais no domicílio').optional(),
  hasCat: z.boolean().describe('Presença de gato').optional(),
  hasDog: z.boolean().describe('Presença de cachorro').optional(),
  hasBird: z.boolean().describe('Presença de pássaro').optional(),
  hasOtherAnimals: z.boolean().describe('Outros animais').optional(),
  hasOtherProfessionalsAttached: z.boolean().describe('Outros profissionais vinculados').optional(),
  microArea: z.string().describe('Microárea').optional(),
  postalCode: z.string().describe('Código postal').optional(),
})

export const residenceExpression = expression(residenceExpressionForCharacteristics.shape)

export const residenceVariable: Variable = {
  key: 'residence',
  actionDefinitionKey: residenceStatsAction.key,
  title: 'Cadastro domiciliar',
  categoryKey: 'healthResidences',
  unitSingular: 'cadastro domiciliar',
  unitPlural: 'cadastros domiciliares',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(residenceExpressionForCharacteristics),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.residence,
  basicSelect: ['postalCode'],
  detailedSelect: [
    'propertyType',
    'waterSupplyType',
    'garbageDisposalType',
    'sanitationType',
    'housingSituation',
    'houseType',
    'landPossessionType',
  ],
  assertions: prepareAssertions(residenceExpression),
}

const residenceStatsDefinition = defineAction({
  ...residenceStatsAction,
  input: z.object({
    variable,
    highlight: residenceExpression.optional(),
    filter: residenceExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(residenceExpressionForCharacteristics),
  }),
  output: z
    .object({
      items: z.array(
        z.object({
          value: z.number().optional(),
          geoId: z.string().optional(),
          geoType: childrenType.optional(),
          h3: z.string().nullish(),
        }),
      ),
      conclusion: z
        .object({
          alert: z.string().nullable().optional(),
          relevance: z.number().optional(),
          insights: z.array(z.any()).optional(),
          space: z.any().nullable().optional(),
        })
        .nullable()
        .optional(),
      componentsDetails: z.any().nullable().optional(),
      componentsResults: z.array(z.any()).optional(),
    })
    .passthrough(),
  cache: input => {
    if (input.filter || input.space?.delimitationType === 'custom') {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
    }
  },
})

export default residenceStatsDefinition
